<template>
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button class="button_back" @click="handleDoItLater">
        <ion-icon name="arrow-back-outline"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-buttons slot="end">
      <ion-button class="pill next-pill-button" @click="handleDoItLater">
        <span>Skip</span>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>

  <div class="main_wrapper ion-padding">
    <div class="container">
      <!-- Header -->
      <div class="auth-header">
        <div class="auth-heading">
          <h1>Pending Agreements</h1>
          <p class="auth-subtext">
            Please review and acknowledge the group agreements below to access shifts from these groups.
          </p>
        </div>
        <div class="brand-icon">
          <SvgIcon name="brandIcon" width="32" height="32" viewBox="0 0 32 32" fill="none" />
        </div>
      </div>

      <!-- Completion Banner when all required are accepted -->
      <div class="completion-banner" v-if="allRequiredAccepted && totalDocumentsCount > 0">
        <div class="banner-icon">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="banner-text">
          <strong>All required agreements accepted!</strong>
          <span>You can now proceed to your dashboard.</span>
        </div>
      </div>

      <!-- Search / Filter Bar (if groups exist) -->
      <div class="agreements-search-box mb-3" v-if="groupsList && groupsList.length > 0">
        <el-input
          v-model="searchQuery"
          placeholder="Search by group name, document, or address..."
          prefix-icon="el-icon-search"
          clearable
          class="custom-search-input"
        />
      </div>

      <!-- Loading State -->
      <div class="pending-docs-loading" v-if="isLoading">
        <div class="skeleton-group-card" v-for="i in 2" :key="i">
          <div class="skeleton-group-header">
            <div class="skeleton-icon"></div>
            <div class="skeleton-header-text">
              <div class="skeleton-line skeleton-title"></div>
              <div class="skeleton-line skeleton-sub"></div>
            </div>
          </div>
          <div class="skeleton-doc-row" v-for="j in 2" :key="j">
            <div class="skeleton-doc-icon"></div>
            <div class="skeleton-doc-info">
              <div class="skeleton-line skeleton-doc-name"></div>
              <div class="skeleton-line skeleton-doc-meta"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State (No Pending Documents) -->
      <div class="pending-docs-empty" v-else-if="filteredGroups.length === 0">
        <div class="empty-icon-circle">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 11L11 13L15 9" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14" stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 10H18C15 10 14 9 14 6V2L22 10Z" stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="empty-title">All Caught Up!</div>
        <div class="empty-subtitle">
          <template v-if="searchQuery">
            No pending agreements match your search "{{ searchQuery }}".
          </template>
          <template v-else>
            You have no pending documents to review or acknowledge at this time.
          </template>
        </div>
        <div class="empty-action">
          <el-button type="blue" class="submit_page" @click="goToDashboard">
            Continue to Dashboard
          </el-button>
        </div>
      </div>

      <!-- Group-wise Documents List -->
      <div class="pending-docs-list-wrapper" v-else>
        <!-- Progress Counter Card -->
        <div class="progress-bar-card" v-if="totalDocumentsCount > 0">
          <div class="progress-info-row">
            <span class="progress-label">
              <i class="el-icon-document-checked"></i> Accepted Agreements
            </span>
            <span class="progress-count">{{ acceptedCount }} of {{ totalDocumentsCount }}</span>
          </div>
          <div class="progress-track">
            <div 
              class="progress-fill" 
              :style="{ width: `${totalDocumentsCount ? (acceptedCount / totalDocumentsCount) * 100 : 0}%` }"
            ></div>
          </div>
        </div>

        <!-- Groups Container -->
        <div class="groups-container">
          <div 
            v-for="(group, gIdx) in filteredGroups" 
            :key="group.id || gIdx"
            class="group-card"
          >
            <!-- Group Header -->
            <div class="group-header">
              <div class="group-header-main">
                <div class="group-avatar-icon">
                  <i class="el-icon-office-building"></i>
                </div>
                <div class="group-title-info">
                  <div class="group-name-row">
                    <h3 class="group-name">{{ group.name }}</h3>
                    <span class="group-badge-count" v-if="group.documents && group.documents.length > 0">
                      {{ group.documents.length }} {{ group.documents.length === 1 ? 'Document' : 'Documents' }}
                    </span>
                  </div>

                  <!-- Group Details -->
                  <div class="group-meta-row" v-if="group.address || group.email || group.phone">
                    <span v-if="group.address" class="group-meta-item group-address">
                      <i class="el-icon-location-outline"></i> {{ group.address }}
                    </span>
                    <span v-if="group.email" class="group-meta-item group-email">
                      <i class="el-icon-message"></i> {{ group.email }}
                    </span>
                    <span v-if="group.phone" class="group-meta-item group-phone">
                      <i class="el-icon-phone-outline"></i> {{ group.phone }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Group Documents List -->
            <div class="group-docs-list" v-if="group.documents && group.documents.length > 0">
              <div 
                v-for="(doc, dIdx) in group.documents" 
                :key="doc.id || dIdx"
                class="doc-item-row"
                :class="{ 'is-accepted': doc.acknowledged, 'is-required': doc.required }"
              >
                <!-- Left Clickable Item Details -->
                <div class="doc-item-main" @click="openDocReview(doc, group)">
                  <!-- Icon -->
                  <div 
                    class="doc-item-icon" 
                    :class="{ 
                      'is-accepted-icon': doc.acknowledged, 
                      'is-pdf': isPdfDoc(doc.url || doc.extension) && !doc.acknowledged,
                      'is-img': isImageDoc(doc.url || doc.extension) && !doc.acknowledged 
                    }"
                  >
                    <i v-if="doc.acknowledged" class="el-icon-check"></i>
                    <i v-else-if="isPdfDoc(doc.url || doc.extension)" class="el-icon-document"></i>
                    <i v-else-if="isImageDoc(doc.url || doc.extension)" class="el-icon-picture-outline"></i>
                    <i v-else class="el-icon-document-checked"></i>
                  </div>

                  <!-- Content -->
                  <div class="doc-item-info">
                    <div class="doc-title-row">
                      <span class="doc-name">{{ doc.name }}</span>
                      <span v-if="doc.required" class="badge-required">Required</span>
                    </div>

                    <div class="doc-meta-row">
                      <span v-if="doc.acknowledged" class="badge-status-accepted">
                        <i class="el-icon-check"></i> Accepted
                      </span>
                      <span v-else class="badge-status-pending">
                        <i class="el-icon-time"></i> Pending
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Small Professional Action Buttons -->
                <div class="doc-item-actions">
                  <button 
                    type="button" 
                    class="btn-doc-small btn-review"
                    :class="{ 'btn-accepted': doc.acknowledged }"
                    @click="openDocReview(doc, group)"
                  >
                    <i :class="doc.acknowledged ? 'el-icon-view' : 'el-icon-edit-outline'"></i>
                    <span>{{ doc.acknowledged ? 'View' : 'Acknowledge' }}</span>
                  </button>

                  <button 
                    type="button" 
                    class="btn-doc-small btn-download"
                    :disabled="downloadingDocId === (doc.id || doc.name)"
                    @click="downloadDoc(doc)"
                    v-if="doc.url && doc.url !== '='"
                    title="Download document"
                  >
                    <i :class="downloadingDocId === (doc.id || doc.name) ? 'el-icon-loading' : 'el-icon-download'"></i>
                    <span class="btn-text-download">Download</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Group Card Footer Actions -->
            <div class="group-card-footer-actions">
              <div class="group-status-hint">
                <span v-if="isGroupInvitationAccepted(group)" class="text-success-hint">
                  <i class="el-icon-circle-check"></i> Group invitation accepted
                </span>
                <span v-else-if="!group.documents || group.documents.length === 0" class="text-success-hint">
                  <i class="el-icon-circle-check"></i> Ready to join group
                </span>
                <span v-else-if="isGroupRequiredAccepted(group)" class="text-success-hint">
                  <i class="el-icon-circle-check"></i> Required agreements accepted
                </span>
                <span v-else class="text-pending-hint">
                  <i class="el-icon-info"></i> Accept required agreements to join group
                </span>
              </div>
              <div v-if="!isGroupInvitationAccepted(group)" class="group-buttons-row">
                <el-button 
                  type="danger" 
                  plain 
                  size="small" 
                  class="btn-group-action btn-group-reject"
                  :loading="processingGroupActionId === (group.id || group.group_id) && currentAction === 'reject'"
                  @click="handleRejectGroup(group)"
                >
                  <i class="el-icon-close mr-1"></i> Reject
                </el-button>

                <el-button 
                  type="primary" 
                  size="small" 
                  class="btn-group-action btn-group-accept"
                  :disabled="!isGroupRequiredAccepted(group)"
                  :loading="processingGroupActionId === (group.id || group.group_id) && currentAction === 'accept'"
                  @click="handleAcceptGroup(group)"
                >
                  <i class="el-icon-check mr-1"></i> Accept
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky Footer CTA & Do It Later -->
        <div class="dashboard-proceed-card">
          <el-button 
            type="blue" 
            class="submit_page w-full btn-proceed"
            @click="goToDashboard"
          >
            <span>Proceed to Dashboard</span>
            <i class="el-icon-right ml-1"></i>
          </el-button>

          <div class="do-it-later-action">
            <button type="button" class="btn-do-it-later" @click="handleDoItLater">
              <i class="el-icon-time mr-1"></i> Do It Later (Skip for now)
            </button>
          </div>
        </div>

        <!-- Help Support -->
        <div class="help-section text-center">
          <p>
            Have questions or issues? Contact
            <a :href="`mailto:${supportEmail}`" class="support-link">{{ supportEmail }}</a>
          </p>
        </div>
      </div>

      <div class="bg-overlay"></div>
    </div>
  </div>

  <div class="background-circles">
    <div class="circle circle-2"></div>
    <div class="circle circle-1"></div>
    <SvgIcon name="bgIcon" class="bg-icon" width="174" height="174" viewBox="0 0 174 174" fill="none" />
  </div>

  <!-- Document Review & Acknowledge Dialog Modal -->
  <el-dialog
    v-model="showDocReviewModal"
    :title="selectedDocForReview ? selectedDocForReview.name : 'Review Document'"
    width="92%"
    custom-class="doc-review-dialog modal_doc_review"
    :destroy-on-close="true"
    :close-on-click-modal="false"
    center
  >
    <div v-if="selectedDocForReview" class="doc-modal-body">
      <!-- Modal Header Meta Bar -->
      <div class="doc-modal-meta-bar">
        <div class="doc-modal-tags">
          <span v-if="selectedDocForReview.required" class="badge-required">Required</span>
          <span v-if="selectedDocForReview.acknowledged" class="badge-status-accepted">
            <i class="el-icon-check"></i> Accepted
          </span>
          <span v-else class="badge-status-pending">
            <i class="el-icon-time"></i> Pending Acceptance
          </span>
          <span v-if="selectedDocForReview.group_name" class="meta-tag meta-group">
            <i class="el-icon-office-building"></i> {{ selectedDocForReview.group_name }}
          </span>
        </div>

        <div class="doc-modal-header-actions" v-if="selectedDocForReview.url && selectedDocForReview.url !== '='">
          <a 
            :href="selectedDocForReview.url" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="btn-modal-action btn-open-external"
          >
            <i class="el-icon-top-right"></i> Open in New Tab
          </a>
          <button 
            type="button" 
            class="btn-modal-action btn-download-modal"
            :disabled="isDownloadingModalDoc"
            @click="downloadDoc(selectedDocForReview, true)"
          >
            <i :class="isDownloadingModalDoc ? 'el-icon-loading' : 'el-icon-download'"></i>
            {{ isDownloadingModalDoc ? 'Downloading...' : 'Download' }}
          </button>
        </div>
      </div>

      <!-- Preview Viewer Container -->
      <div class="doc-preview-viewport">
        <!-- Image Preview -->
        <div v-if="isImageDoc(selectedDocForReview.url || selectedDocForReview.extension)" class="doc-image-preview-container">
          <img 
            :src="selectedDocForReview.url" 
            :alt="selectedDocForReview.name" 
            class="doc-preview-img"
          />
        </div>

        <!-- PDF / Iframe Preview -->
        <div v-else-if="selectedDocForReview.url && selectedDocForReview.url !== '='" class="doc-iframe-container">
          <iframe 
            :src="selectedDocForReview.url" 
            class="doc-preview-iframe"
            title="PDF Preview"
            frameborder="0"
          ></iframe>
        </div>

        <!-- No URL / Fallback Box -->
        <div v-else class="doc-no-preview-box">
          <i class="el-icon-document text-muted"></i>
          <p>No preview file is available for this document.</p>
        </div>
      </div>

      <!-- Legal / Terms Acknowledgement Notice -->
      <div class="doc-ack-notice-box" :class="{ 'is-ack-done': selectedDocForReview.acknowledged }">
        <div class="ack-notice-icon">
          <i :class="selectedDocForReview.acknowledged ? 'el-icon-check' : 'el-icon-info'"></i>
        </div>
        <div class="ack-notice-text">
          <template v-if="selectedDocForReview.acknowledged">
            <strong>Document Acknowledged.</strong>
            <span>You have accepted and agreed to this document.</span>
          </template>
          <template v-else>
            <strong>Acknowledgement Confirmation:</strong>
            <span>
              By clicking <strong>"Acknowledge"</strong>, you confirm that you have read, understood, and agreed to all terms and conditions outlined in this document.
            </span>
          </template>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer-actions">
        <el-button @click="showDocReviewModal = false" class="el-button--grey">
          Close
        </el-button>
        <el-button 
          type="primary" 
          class="el-button--blue"
          :loading="isAcknowledgingDoc"
          :disabled="selectedDocForReview && selectedDocForReview.acknowledged"
          @click="confirmAcknowledgeDoc"
        >
          <span v-if="selectedDocForReview && selectedDocForReview.acknowledged">
            <i class="el-icon-check"></i> Already Acknowledged
          </span>
          <span v-else>
            <i class="el-icon-check"></i> Acknowledge
          </span>
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, onMounted, inject } from "vue";
import { ElMessage, ElMessageBox } from "@/components/Vendor/Element";
import { addIcons } from "ionicons";
import { arrowBackOutline } from "ionicons/icons";
import { IonToolbar, IonButton, IonButtons, IonIcon } from "@ionic/vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import SvgIcon from "@/components/Svg/SvgIcon.vue";

addIcons({
  "arrow-back-outline": arrowBackOutline,
});

export default {
  name: "PendingGroupManagerDocuments",
  components: {
    IonToolbar,
    IonButton,
    IonButtons,
    IonIcon,
    SvgIcon,
  },
  setup() {
    const $store = useStore();
    const $router = useRouter();
    const route = useRoute();
    const supportEmail = process.env.VUE_APP_SUPPORT_EMAIL || "support@locumate.com.au";
    const setPageClassGlobal = inject("setPageClassGlobal", () => {});

    const isLoading = ref(true);
    const groupsList = ref([]);
    const searchQuery = ref("");
    const showDocReviewModal = ref(false);
    const selectedDocForReview = ref(null);
    const isAcknowledgingDoc = ref(false);
    const isDownloadingModalDoc = ref(false);
    const downloadingDocId = ref(null);

    const isUuid = (val) => {
      if (!val || typeof val !== "string") return false;
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val.trim());
    };

    const extractInvitationId = (obj) => {
      if (!obj || typeof obj !== "object") return null;

      const candidates = [
        obj.invitation_id,
        obj.invitationId,
        obj.invitation?.id,
        obj.invitation?.uuid,
        obj.invitation_uuid,
        obj.invitationUuid,
        obj.internal_pool_invitation_id,
        obj.internal_pool_invitation?.id,
        obj.locumate_internal_pool_invitation_id,
        obj.pool_invitation_id,
        obj.acceptance_tracking?.invitation_id,
        obj.acceptance_tracking?.internal_pool_invitation_id,
        obj.acceptance_tracking_invitation_id,
      ];

      for (const c of candidates) {
        if (c && typeof c === "string" && c.trim() !== "") {
          return c.trim();
        }
        if (c && typeof c === "number") {
          return String(c);
        }
      }

      if (isUuid(obj.id)) return String(obj.id).trim();
      if (isUuid(obj.uuid)) return String(obj.uuid).trim();

      return null;
    };

    const extractInvitationStatus = (obj) => {
      if (!obj || typeof obj !== "object") return null;
      return (
        obj.invitation_status ||
        obj.invitationStatus ||
        obj.invitation?.status ||
        obj.invitation?.invitation_status ||
        obj.acceptance_tracking?.invitation_status ||
        null
      );
    };

    const isGenericDefaultName = (name) => {
      if (!name || typeof name !== "string") return true;
      const lower = name.toLowerCase().trim();
      return (
        lower === "" ||
        lower === "document" ||
        lower === "documents" ||
        lower === "default" ||
        lower === "default document" ||
        lower === "default agreement" ||
        lower === "agreement" ||
        lower === "agreements" ||
        lower === "group agreement" ||
        lower === "group agreements" ||
        lower === "group document" ||
        lower === "group documents" ||
        lower === "onboarding agreement" ||
        lower === "onboarding document" ||
        lower === "null" ||
        lower === "undefined" ||
        lower === "sample" ||
        lower === "file"
      );
    };

    const extractDocumentName = (doc, groupName = "") => {
      if (!doc) return "Agreement";
      if (typeof doc === "string" && doc.trim()) {
        const s = doc.trim();
        if (!isGenericDefaultName(s)) return s;
      }

      // Check all direct keys on doc object
      const directCandidates = [
        doc.document_name,
        doc.documentName,
        doc.doc_name,
        doc.docName,
        doc.document_title,
        doc.documentTitle,
        doc.doc_title,
        doc.docTitle,
        doc.user_friendly_name,
        doc.userFriendlyName,
        doc.user_friendly_document_name,
        doc.userFriendlyDocumentName,
        doc.file_name,
        doc.fileName,
        doc.filename,
        doc.original_filename,
        doc.original_name,
        doc.originalName,
        doc.display_name,
        doc.displayName,
        doc.title,
        doc.agreement_name,
        doc.agreementName,
        doc.label,
        doc.heading,
        doc.onboarding_document_name,
        doc.group_document_name,
      ];

      for (const n of directCandidates) {
        if (n && typeof n === "string" && !isGenericDefaultName(n)) {
          const trimmed = n.trim();
          if (!groupName || trimmed.toLowerCase() !== groupName.toLowerCase().trim()) {
            return trimmed;
          }
        }
      }

      // Check nested objects
      const nestedObjects = [
        doc.document,
        doc.onboarding_document,
        doc.onboardingDocument,
        doc.group_document,
        doc.groupDocument,
        doc.group_manager_document,
        doc.groupManagerDocument,
        doc.shift_document,
        doc.shiftDocument,
      ];

      for (const nested of nestedObjects) {
        if (nested) {
          if (typeof nested === "string" && !isGenericDefaultName(nested)) {
            return nested.trim();
          }
          if (typeof nested === "object") {
            const nestedCandidates = [
              nested.document_name,
              nested.documentName,
              nested.doc_name,
              nested.docName,
              nested.user_friendly_name,
              nested.userFriendlyName,
              nested.user_friendly_document_name,
              nested.title,
              nested.file_name,
              nested.fileName,
              nested.filename,
              nested.original_filename,
              nested.original_name,
              nested.name,
              nested.display_name,
              nested.label,
            ];
            for (const n of nestedCandidates) {
              if (n && typeof n === "string" && !isGenericDefaultName(n)) {
                const trimmed = n.trim();
                if (!groupName || trimmed.toLowerCase() !== groupName.toLowerCase().trim()) {
                  return trimmed;
                }
              }
            }
          }
        }
      }

      // Check doc.name only if it doesn't match groupName and not generic
      if (doc.name && typeof doc.name === "string" && !isGenericDefaultName(doc.name)) {
        const trimmed = doc.name.trim();
        if (!groupName || trimmed.toLowerCase() !== groupName.toLowerCase().trim()) {
          return trimmed;
        }
      }

      // Try URL / file extraction
      const urlCandidates = [
        doc.url,
        doc.document_url,
        doc.file_url,
        doc.link,
        doc.file,
        doc.file_path,
        doc.path,
        doc.document?.url,
        doc.onboarding_document?.url,
        doc.group_document?.url,
      ];

      for (const u of urlCandidates) {
        if (u && typeof u === "string" && u.includes("/") && u !== "=") {
          try {
            const cleanUrl = u.split("?")[0];
            const filename = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
            if (filename && filename.length > 2) {
              const decoded = decodeURIComponent(filename);
              const nameWithoutExt = decoded.replace(/\.[^/.]+$/, "");
              if (nameWithoutExt && !isGenericDefaultName(nameWithoutExt)) {
                return nameWithoutExt.replace(/[-_]/g, " ").trim();
              }
            }
          } catch (e) {
            // ignore
          }
        }
      }

      // If document ID or tracking ID is available, provide a clean identifiable label
      const docId = doc.document_id || doc.id || doc.history_id || doc.acceptance_tracking_id;
      if (docId) {
        return `Agreement #${docId}`;
      }

      return "Agreement";
    };

    const extractGroupName = (item) => {
      if (!item) return "Group";
      const possibleGroupNames = [
        item.group_name,
        item.groupname,
        item.groupName,
        item.pharmacy_name,
        item.pharmacyName,
        item.employer_name,
        item.employerName,
        item.name,
        item.title,
      ];

      for (const g of possibleGroupNames) {
        if (g && typeof g === "string" && g.trim() !== "" && !isGenericDefaultName(g)) {
          return g.trim();
        }
      }

      return "Group";
    };

    const normalizeGroups = (data) => {
      if (!data) return [];
      let rawGroups = [];

      const dataRootInvId =
        extractInvitationId(data) ||
        extractInvitationId(data?.data) ||
        extractInvitationId(data?.data?.data) ||
        data?.invitation_id ||
        data?.data?.invitation_id ||
        storedRootInvitationId.value ||
        null;

      if (data.documents && Array.isArray(data.documents)) {
        rawGroups = data.documents;
      } else if (data.data && Array.isArray(data.data.documents)) {
        rawGroups = data.data.documents;
      } else if (data.data && data.data.data && Array.isArray(data.data.data.documents)) {
        rawGroups = data.data.data.documents;
      } else if (data.pending_documents && Array.isArray(data.pending_documents)) {
        rawGroups = data.pending_documents;
      } else if (data.pendingDocuments && Array.isArray(data.pendingDocuments)) {
        rawGroups = data.pendingDocuments;
      } else if (data.data && Array.isArray(data.data)) {
        rawGroups = data.data;
      } else if (Array.isArray(data)) {
        rawGroups = data;
      } else if (data.data && typeof data.data === "object") {
        if (Array.isArray(data.data.documents)) {
          rawGroups = data.data.documents;
        } else {
          rawGroups = [data.data];
        }
      } else if (typeof data === "object") {
        rawGroups = [data];
      }

      const groups = [];

      rawGroups.forEach((item, idx) => {
        if (!item) return;

        const groupName = extractGroupName(item);

        const docListRaw =
          item.groupdocument ||
          item.group_document ||
          item.group_documents ||
          item.groupdocuments ||
          item.groupDocument ||
          item.groupDocuments ||
          item.documents ||
          item.docs ||
          item.group_manager_documents ||
          item.groupManagerDocuments ||
          item.onboarding_documents ||
          item.onboardingDocuments;

        let docList = null;
        if (Array.isArray(docListRaw)) {
          docList = docListRaw;
        } else if (docListRaw && typeof docListRaw === "object") {
          if (
            docListRaw.document_name ||
            docListRaw.document_id ||
            docListRaw.history_id ||
            docListRaw.url ||
            docListRaw.extension ||
            docListRaw.size ||
            docListRaw.id
          ) {
            docList = [docListRaw];
          } else {
            const vals = Object.values(docListRaw);
            if (vals.length > 0 && typeof vals[0] === "object" && vals[0] !== null) {
              docList = vals;
            } else {
              docList = [docListRaw];
            }
          }
        } else if (item.document && typeof item.document === "object") {
          if (Array.isArray(item.document)) {
            docList = item.document;
          } else if (item.document.document_name || item.document.id || item.document.name || item.document.url) {
            docList = [item.document];
          }
        }

        if (docList && Array.isArray(docList) && docList.length > 0) {
          const groupUserId =
            item.group_user_id ||
            item.group_manager_id ||
            item.owner_id ||
            item.user_id ||
            null;

          let invitationId = extractInvitationId(item) || dataRootInvId;
          let invitationStatus = extractInvitationStatus(item);
          if (docList && Array.isArray(docList)) {
            for (const d of docList) {
              if (!invitationId) {
                const dInv = extractInvitationId(d);
                if (dInv) invitationId = dInv;
              }
              if (!invitationStatus) {
                const dStatus = extractInvitationStatus(d);
                if (dStatus) invitationStatus = dStatus;
              }
            }
          }

          const groupAcceptanceTrackingId =
            item.acceptance_tracking_id ||
            item.acceptanceTrackingId ||
            item.tracking_id ||
            item.acceptance_id ||
            (docList && (docList[0]?.acceptance_tracking_id || docList[0]?.acceptanceTrackingId || docList[0]?.tracking_id)) ||
            null;

          const normalizedDocs = docList.map((doc, dIdx) => {
            const docObj = (doc && typeof doc === "object") ? doc : { name: String(doc) };
            const docName = extractDocumentName(docObj, groupName);
            const docGroupUserId =
              docObj.group_user_id ||
              docObj.group_manager_id ||
              groupUserId;
            const docInvitationId = extractInvitationId(docObj) || invitationId;
            const docInvitationStatus = extractInvitationStatus(docObj) || invitationStatus;
            const docAcceptanceTrackingId =
              docObj.acceptance_tracking_id ||
              docObj.acceptanceTrackingId ||
              docObj.tracking_id ||
              docObj.acceptance_id ||
              groupAcceptanceTrackingId;

            return {
              ...docObj,
              id: docObj.document_id || docObj.id || docObj.history_id || `doc_${idx}_${dIdx}`,
              document_id: docObj.document_id || docObj.id,
              history_id: docObj.history_id,
              acceptance_tracking_id: docAcceptanceTrackingId,
              group_user_id: docGroupUserId,
              invitation_id: docInvitationId,
              invitation_status: docInvitationStatus,
              name: docName,
              size: docObj.size ? `${docObj.size} MB` : "",
              extension: docObj.extension || docObj.ext || "",
              required: docObj.required === true || docObj.required === 1 || docObj.required === "1" || docObj.required === "true" || docObj.required_to_accept_shifts === true || docObj.requiredToAcceptShifts === true,
              status: docObj.status || "pending",
              url: docObj.url || docObj.document_url || docObj.link || docObj.file_url || "",
              acknowledged: !!docObj.acknowledged || !!docObj.accepted || docObj.acknowledgement_status === "accepted" || docObj.acknowledgementStatus === "accepted" || docObj.status === "accepted" || false,
              created_at: docObj.created_at,
              updated_at: docObj.updated_at,
              group_id: item.group_id || item.id,
              group_name: groupName,
            };
          });

          const existingGroup = groups.find(
            (g) => (g.id && item.id && g.id === (item.group_id || item.id)) || g.name === groupName
          );

          if (existingGroup) {
            if (!existingGroup.group_user_id && groupUserId) {
              existingGroup.group_user_id = groupUserId;
            }
            if (!existingGroup.invitation_id && invitationId) {
              existingGroup.invitation_id = invitationId;
            }
            if (!existingGroup.invitation_status && invitationStatus) {
              existingGroup.invitation_status = invitationStatus;
            }
            if (!existingGroup.acceptance_tracking_id && groupAcceptanceTrackingId) {
              existingGroup.acceptance_tracking_id = groupAcceptanceTrackingId;
            }
            existingGroup.documents.push(...normalizedDocs);
            if (!existingGroup.invitation_id) {
              const dWithInv = existingGroup.documents.find((d) => d.invitation_id);
              if (dWithInv) {
                existingGroup.invitation_id = dWithInv.invitation_id;
              }
            }
            if (!existingGroup.invitation_status) {
              const dWithStatus = existingGroup.documents.find((d) => d.invitation_status);
              if (dWithStatus) {
                existingGroup.invitation_status = dWithStatus.invitation_status;
              }
            }
          } else {
            const dWithInv = normalizedDocs.find((d) => d.invitation_id);
            const finalInvId = invitationId || (dWithInv ? dWithInv.invitation_id : null) || dataRootInvId;
            const dWithStatus = normalizedDocs.find((d) => d.invitation_status);
            const finalInvStatus = invitationStatus || (dWithStatus ? dWithStatus.invitation_status : null);

            groups.push({
              id: item.group_id || item.id || `group_${idx}`,
              group_id: item.group_id || item.id,
              group_user_id: groupUserId,
              acceptance_tracking_id: groupAcceptanceTrackingId,
              invitation_id: finalInvId,
              invitation_status: finalInvStatus,
              name: groupName,
              address: item.address || "",
              phone: item.phone || "",
              email: item.email || "",
              documents: normalizedDocs,
            });
          }
        } else if (docListRaw !== undefined || item.groupdocument !== undefined || item.group_document !== undefined || item.group_documents !== undefined || item.group_name !== undefined || item.groupname !== undefined || item.groupName !== undefined || item.pharmacy_name !== undefined || item.pharmacyName !== undefined || item.group_id !== undefined || item.group_user_id !== undefined) {
          // Group has NO documents (e.g. "groupdocument": [])
          const groupUserId =
            item.group_user_id ||
            item.group_manager_id ||
            item.owner_id ||
            item.user_id ||
            null;
          const docInvitationId = extractInvitationId(item) || dataRootInvId;
          const docInvitationStatus = extractInvitationStatus(item);
          const groupAcceptanceTrackingId =
            item.acceptance_tracking_id ||
            item.acceptanceTrackingId ||
            item.tracking_id ||
            item.acceptance_id ||
            null;

          let existing = groups.find(
            (g) => (g.id && item.id && g.id === (item.group_id || item.id)) || g.name === groupName
          );
          if (!existing) {
            existing = {
              id: item.group_id || item.id || `group_${groups.length}`,
              group_id: item.group_id || item.id,
              group_user_id: groupUserId,
              invitation_id: docInvitationId,
              invitation_status: docInvitationStatus,
              acceptance_tracking_id: groupAcceptanceTrackingId,
              name: groupName,
              address: item.address || "",
              phone: item.phone || "",
              email: item.email || "",
              documents: [],
            };
            groups.push(existing);
          } else {
            if (!existing.invitation_id && docInvitationId) {
              existing.invitation_id = docInvitationId;
            }
            if (!existing.invitation_status && docInvitationStatus) {
              existing.invitation_status = docInvitationStatus;
            }
            if (!existing.group_user_id && groupUserId) {
              existing.group_user_id = groupUserId;
            }
            if (!existing.acceptance_tracking_id && groupAcceptanceTrackingId) {
              existing.acceptance_tracking_id = groupAcceptanceTrackingId;
            }
          }
        } else if (item.document_name || item.url || item.document_url || item.link || item.file_url) {
          // Flat document fallback
          const docName = extractDocumentName(item, groupName);
          const fallbackGroupName = item.group_name || item.groupname || item.pharmacy_name || "General Group";
          const groupUserId =
            item.group_user_id ||
            item.group_manager_id ||
            item.owner_id ||
            item.user_id ||
            null;
          const docInvitationId = extractInvitationId(item) || dataRootInvId;
          const docInvitationStatus = extractInvitationStatus(item);

          let existing = groups.find((g) => g.name === fallbackGroupName);
          if (!existing) {
            existing = {
              id: item.group_id || item.id || `group_${groups.length}`,
              group_id: item.group_id || item.id,
              group_user_id: groupUserId,
              invitation_id: docInvitationId,
              invitation_status: docInvitationStatus,
              acceptance_tracking_id: item.acceptance_tracking_id,
              name: fallbackGroupName,
              address: item.address || "",
              phone: item.phone || "",
              email: item.email || "",
              documents: [],
            };
            groups.push(existing);
          } else {
            if (!existing.invitation_id && docInvitationId) {
              existing.invitation_id = docInvitationId;
            }
            if (!existing.invitation_status && docInvitationStatus) {
              existing.invitation_status = docInvitationStatus;
            }
          }
          existing.documents.push({
            ...item,
            id: item.document_id || item.id || `doc_${existing.documents.length}`,
            document_id: item.document_id || item.id,
            history_id: item.history_id,
            acceptance_tracking_id: item.acceptance_tracking_id,
            group_user_id: groupUserId,
            invitation_id: docInvitationId || existing.invitation_id || null,
            invitation_status: docInvitationStatus || existing.invitation_status || null,
            name: docName,
            size: item.size ? `${item.size} MB` : "",
            extension: item.extension || item.ext || "",
            required: item.required === true || item.required === 1 || item.required === "1" || item.required === "true",
            status: item.status || "pending",
            url: item.url || item.document_url || item.link || item.file_url || "",
            created_at: item.created_at,
            updated_at: item.updated_at,
            acknowledged: !!item.acknowledged || !!item.accepted || item.acknowledgement_status === "accepted" || item.status === "accepted" || false,
            group_name: fallbackGroupName,
          });
        }
      });

      return groups.filter((g) => g && g.name);
    };

    const allDocuments = computed(() => {
      return groupsList.value.flatMap((g) => g.documents || []);
    });

    const totalDocumentsCount = computed(() => {
      return allDocuments.value.length;
    });

    const acceptedCount = computed(() => {
      return allDocuments.value.filter((d) => d.acknowledged).length;
    });

    const allRequiredAccepted = computed(() => {
      if (allDocuments.value.length === 0) {
        return true;
      }
      const requiredDocs = allDocuments.value.filter((doc) => doc.required);
      if (requiredDocs.length === 0) {
        return allDocuments.value.every((doc) => doc.acknowledged);
      }
      return requiredDocs.every((doc) => doc.acknowledged);
    });

    const filteredGroups = computed(() => {
      if (!groupsList.value || groupsList.value.length === 0) return [];
      if (!searchQuery.value || !searchQuery.value.trim()) return groupsList.value;

      const q = searchQuery.value.toLowerCase().trim();
      return groupsList.value
        .map((group) => {
          const groupNameMatch = group.name && group.name.toLowerCase().includes(q);
          const addressMatch = group.address && group.address.toLowerCase().includes(q);
          const emailMatch = group.email && group.email.toLowerCase().includes(q);

          if (groupNameMatch || addressMatch || emailMatch) {
            return group;
          }

          const matchedDocs = group.documents.filter((doc) => {
            return doc.name && doc.name.toLowerCase().includes(q);
          });

          if (matchedDocs.length > 0) {
            return {
              ...group,
              documents: matchedDocs,
            };
          }

          return null;
        })
        .filter(Boolean);
    });

    const storedRootInvitationId = ref(null);

    const fetchPendingDocuments = async () => {
      try {
        isLoading.value = true;
        const res = await $store.dispatch("GET_PENDING_GROUP_MANAGER_DOCUMENTS");
        console.log("GET_PENDING_GROUP_MANAGER_DOCUMENTS response:", res);
        storedRootInvitationId.value =
          extractInvitationId(res) ||
          extractInvitationId(res?.data) ||
          extractInvitationId(res?.data?.data) ||
          res?.invitation_id ||
          res?.data?.invitation_id ||
          null;
        groupsList.value = normalizeGroups(res);
      } catch (err) {
        console.warn("Error fetching pending group manager documents:", err);
        groupsList.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const openDocReview = (doc, group = null) => {
      const docData = { ...doc };
      if (!docData.group_user_id) {
        if (group && (group.group_user_id || group.group_manager_id)) {
          docData.group_user_id = group.group_user_id || group.group_manager_id;
        } else if (groupsList.value) {
          const parentGroup = groupsList.value.find((g) =>
            g.documents && g.documents.some((d) => d.id === doc.id || d.document_id === doc.document_id)
          );
          if (parentGroup && (parentGroup.group_user_id || parentGroup.group_manager_id)) {
            docData.group_user_id = parentGroup.group_user_id || parentGroup.group_manager_id;
          }
        }
      }
      selectedDocForReview.value = docData;
      showDocReviewModal.value = true;
    };

    const isImageDoc = (pathOrExt) => {
      if (!pathOrExt) return false;
      const clean = pathOrExt.split("?")[0].toLowerCase();
      return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(clean) || ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(clean);
    };

    const isPdfDoc = (pathOrExt) => {
      if (!pathOrExt) return false;
      const clean = pathOrExt.split("?")[0].toLowerCase();
      return /\.pdf$/i.test(clean) || clean === "pdf";
    };

    const downloadDoc = async (doc, fromModal = false) => {
      if (!doc || !doc.url || doc.url === "=") return;
      try {
        if (fromModal) {
          isDownloadingModalDoc.value = true;
        } else {
          downloadingDocId.value = doc.id || doc.name;
        }

        let extension = doc.extension ? `.${doc.extension}` : "";
        if (!extension) {
          try {
            const path = doc.url.split("?")[0];
            const parts = path.split(".");
            if (parts.length > 1) {
              extension = `.${parts.pop()}`;
            }
          } catch (e) {
            extension = "";
          }
        }

        let fileName = (doc.name || "document").replace(/[^a-zA-Z0-9_-]/g, "_");
        if (extension && !fileName.toLowerCase().endsWith(extension.toLowerCase())) {
          fileName += extension;
        }

        const response = await fetch(doc.url);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        ElMessage({
          type: "success",
          message: "Document downloaded successfully!",
        });
      } catch (error) {
        console.warn("Direct blob download failed, falling back to window.open", error);
        window.open(doc.url, "_blank");
      } finally {
        if (fromModal) {
          isDownloadingModalDoc.value = false;
        } else {
          downloadingDocId.value = null;
        }
      }
    };

    const confirmAcknowledgeDoc = async () => {
      if (!selectedDocForReview.value) return;
      try {
        isAcknowledgingDoc.value = true;
        const payload = {
          document_id: selectedDocForReview.value.document_id || selectedDocForReview.value.id,
          status: "accepted",
        };

        const groupUserId =
          selectedDocForReview.value.group_user_id ||
          selectedDocForReview.value.group_manager_id;

        if (groupUserId) {
          payload.group_user_id = groupUserId;
        }

        const invId = extractInvitationId(selectedDocForReview.value);
        if (invId) {
          payload.invitation_id = invId;
        }

        await $store.dispatch("LOCUM_ACKNOWLEDGE_ONBOARDING_DOCUMENT", payload);

        selectedDocForReview.value.acknowledged = true;
        selectedDocForReview.value.status = "accepted";

        // Update in groupsList
        groupsList.value.forEach((group) => {
          const found = group.documents.find(
            (d) => d.id === selectedDocForReview.value.id || d.document_id === selectedDocForReview.value.document_id
          );
          if (found) {
            found.acknowledged = true;
            found.status = "accepted";
          }
        });

        ElMessage({
          type: "success",
          message: `${selectedDocForReview.value.name} acknowledged successfully!`,
        });

        showDocReviewModal.value = false;
      } catch (error) {
        console.error("Failed to acknowledge document:", error);
        ElMessage({
          type: "error",
          message: error?.message || "Failed to acknowledge document. Please try again.",
        });
      } finally {
        isAcknowledgingDoc.value = false;
      }
    };

    const goToDashboard = () => {
      sessionStorage.setItem("skip_pending_docs_prompt", "true");
      const userData = $store.getters.GET_USER_DATA;
      if (userData?.role === "locum" && userData?.contractorOnboarding) {
        $router.push({ name: "welcome_contractor" });
      } else {
        $router.push({ name: "search" });
      }
    };

    const handleDoItLater = () => {
      sessionStorage.setItem("skip_pending_docs_prompt", "true");
      goToDashboard();
    };

    const processingGroupActionId = ref(null);
    const currentAction = ref(null);

    const isGroupRequiredAccepted = (group) => {
      if (!group || !group.documents || group.documents.length === 0) return true;
      const requiredDocs = group.documents.filter((doc) => doc.required);
      if (requiredDocs.length > 0) {
        return requiredDocs.every((doc) => doc.acknowledged);
      }
      return group.documents.every((doc) => doc.acknowledged);
    };

    const isGroupInvitationAccepted = (group) => {
      if (!group) return false;
      const groupStatus =
        group.invitation_status ||
        group.invitationStatus ||
        group.invitation?.status;

      if (
        typeof groupStatus === "string" &&
        groupStatus.trim().toLowerCase() === "accepted"
      ) {
        return true;
      }

      if (group.documents && Array.isArray(group.documents)) {
        return group.documents.some((d) => {
          if (!d) return false;
          const docStatus =
            d.invitation_status ||
            d.invitationStatus ||
            d.invitation?.status ||
            d.acceptance_tracking?.invitation_status;
          return (
            typeof docStatus === "string" &&
            docStatus.trim().toLowerCase() === "accepted"
          );
        });
      }

      return false;
    };

    const handleRejectGroup = async (group) => {
      if (!group) return;

      try {
        await ElMessageBox.confirm(
          `Are you sure you want to decline/reject agreements from ${group.name}?`,
          "Reject Group Agreements",
          {
            confirmButtonText: "Yes, Reject",
            cancelButtonText: "Cancel",
            type: "warning",
            confirmButtonClass: "el-button--danger",
          }
        );
      } catch {
        return;
      }

      processingGroupActionId.value = group.id || group.group_id;
      currentAction.value = "reject";

      try {
        const docs = (group.documents && group.documents.length > 0) ? group.documents : [group];
        const firstDoc = docs[0] || {};

        const groupId =
          group.group_id ||
          firstDoc.group_id ||
          group.id ||
          null;

        const trackingId =
          firstDoc.acceptance_tracking_id ||
          group.acceptance_tracking_id ||
          firstDoc.acceptanceTrackingId ||
          group.acceptanceTrackingId ||
          null;

        const groupUserId =
          firstDoc.group_user_id ||
          group.group_user_id ||
          firstDoc.group_manager_id ||
          null;

        const docId =
          firstDoc.document_id ||
          firstDoc.id ||
          group.document_id ||
          group.id ||
          null;

        const payload = {
          group_id: groupId,
          group_user_id: groupUserId,
          acceptance_tracking_id: trackingId,
          document_id: docId,
        };

        console.log("Reject group API payload:", payload);

        // Call the reject-group API (/locum/onboarding-document/reject-group)
        await $store.dispatch("LOCUM_REJECT_GROUP_ONBOARDING_DOCUMENT", payload);

        // If there are multiple documents in this group, reject remaining ones as well
        if (docs.length > 1) {
          for (let i = 1; i < docs.length; i++) {
            const extraDoc = docs[i];
            const extraPayload = {
              group_id: groupId,
              group_user_id:
                extraDoc.group_user_id ||
                group.group_user_id ||
                extraDoc.group_manager_id ||
                null,
              acceptance_tracking_id:
                extraDoc.acceptance_tracking_id ||
                group.acceptance_tracking_id ||
                extraDoc.acceptanceTrackingId ||
                group.acceptanceTrackingId ||
                null,
              document_id:
                extraDoc.document_id ||
                extraDoc.id ||
                null,
            };
            try {
              await $store.dispatch("LOCUM_REJECT_GROUP_ONBOARDING_DOCUMENT", extraPayload);
            } catch (extraErr) {
              console.warn("Extra document reject sync notice:", extraErr);
            }
          }
        }

        // Also sync internal pool if invitation_id is present
        const invId = group.invitation_id || (group.documents && group.documents[0]?.invitation_id);
        if (invId) {
          try {
            await $store.dispatch("INTERNAL_POOL_REJECT", {
              invitationId: invId,
            });
          } catch (invErr) {
            console.warn("Group reject internal pool notice:", invErr);
          }
        }

        // Remove group from list
        groupsList.value = groupsList.value.filter(
          (g) => (g.id || g.group_id) !== (group.id || group.group_id)
        );

        ElMessage({
          type: "success",
          message: `${group.name} agreements have been declined.`,
        });
      } catch (err) {
        console.error("Error rejecting group:", err);
        ElMessage({
          type: "error",
          message: err?.message || err?.error || "Failed to reject group. Please try again.",
        });
      } finally {
        processingGroupActionId.value = null;
        currentAction.value = null;
      }
    };

    const handleAcceptGroup = async (group) => {
      if (!group) return;

      console.log("handleAcceptGroup clicked for group:", group);

      if (!isGroupRequiredAccepted(group)) {
        ElMessage({
          type: "warning",
          message: `Please review and accept all required documents for ${group.name} first.`,
        });
        return;
      }

      processingGroupActionId.value = group.id || group.group_id;
      currentAction.value = "accept";

      try {
        const docs = (group.documents && group.documents.length > 0) ? group.documents : [];

        // Collect all possible invitation IDs from group, documents, stored root, route query/params
        const invIds = new Set();
        const gInv = extractInvitationId(group) || group.invitation_id;
        if (gInv) invIds.add(gInv);

        docs.forEach((d) => {
          const dInv = extractInvitationId(d) || d.invitation_id;
          if (dInv) invIds.add(dInv);
        });

        if (storedRootInvitationId.value) invIds.add(storedRootInvitationId.value);
        if (route.query && route.query.invitation_id) invIds.add(route.query.invitation_id);
        if (route.query && route.query.invitationId) invIds.add(route.query.invitationId);
        if (route.query && route.query.id) invIds.add(route.query.id);
        if (route.params && route.params.invitationId) invIds.add(route.params.invitationId);
        if (route.params && route.params.id) invIds.add(route.params.id);

        console.log("Accepting group agreements. Found invitation IDs:", Array.from(invIds));

        // 1. Call internal-pool accept API if invitation_id is present
        if (invIds.size > 0) {
          for (const id of invIds) {
            console.log(`Calling accept/internal-pool API with invitation_id: ${id}`);
            try {
              await $store.dispatch("INTERNAL_POOL_ACCEPT", {
                invitationId: id,
                device_name: "Win32",
              });
            } catch (invErr) {
              console.warn(`Internal pool accept API notice for ${id}:`, invErr);
            }
          }
        } else {
          console.warn("No invitation_id found for group accept:", group);
        }

        // 2. Also ensure all documents in this group are acknowledged via API
        for (const doc of docs) {
          if (doc.document_id || doc.id) {
            try {
              const ackPayload = {
                document_id: doc.document_id || doc.id,
                status: "accepted",
              };
              if (doc.group_user_id || group.group_user_id) {
                ackPayload.group_user_id = doc.group_user_id || group.group_user_id;
              }
              const dInv = extractInvitationId(doc) || extractInvitationId(group) || storedRootInvitationId.value;
              if (dInv) {
                ackPayload.invitation_id = dInv;
              }
              console.log("Calling LOCUM_ACKNOWLEDGE_ONBOARDING_DOCUMENT:", ackPayload);
              await $store.dispatch("LOCUM_ACKNOWLEDGE_ONBOARDING_DOCUMENT", ackPayload);
            } catch (ackErr) {
              console.warn("Auto-acknowledge notice:", ackErr);
            }
          }
        }

        // Mark all docs in this group as acknowledged
        group.documents.forEach((d) => {
          d.acknowledged = true;
          d.status = "accepted";
        });

        // Remove accepted group from list
        groupsList.value = groupsList.value.filter(
          (g) => (g.id || g.group_id) !== (group.id || group.group_id)
        );

        ElMessage({
          type: "success",
          message: `You have successfully accepted ${group.name} agreements!`,
        });
      } catch (err) {
        console.error("Error accepting group:", err);
        ElMessage({
          type: "error",
          message: err?.message || "Failed to accept group. Please try again.",
        });
      } finally {
        processingGroupActionId.value = null;
        currentAction.value = null;
      }
    };

    onMounted(() => {
      if (setPageClassGlobal) {
        setPageClassGlobal("global_auth_page global_pending_documents_page");
      }
      fetchPendingDocuments();
    });

    return {
      isLoading,
      groupsList,
      filteredGroups,
      totalDocumentsCount,
      acceptedCount,
      allRequiredAccepted,
      searchQuery,
      showDocReviewModal,
      selectedDocForReview,
      isAcknowledgingDoc,
      isDownloadingModalDoc,
      downloadingDocId,
      supportEmail,
      openDocReview,
      isImageDoc,
      isPdfDoc,
      downloadDoc,
      confirmAcknowledgeDoc,
      goToDashboard,
      handleDoItLater,
      isGroupRequiredAccepted,
      isGroupInvitationAccepted,
      handleRejectGroup,
      handleAcceptGroup,
      processingGroupActionId,
      currentAction,
    };
  },
};
</script>

<style lang="scss" scoped>
.global_pending_documents_page {
  background-color: var(--color-gray-bg, #f8fafc);
  min-height: 100vh;
}

.button_back {
  --color: #334155;
  font-size: 20px;
}

.button_skip {
  --color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  .skip-text {
    color: #2563eb;
    padding: 4px 10px;
    background: #eff6ff;
    border-radius: 6px;
    border: 1px solid #bfdbfe;
    transition: all 0.2s ease;
    &:hover {
      background: #dbeafe;
    }
  }
}

.auth-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;

  .auth-heading {
    h1 {
      font-size: 22px;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 4px;
      line-height: 1.25;
    }
    .auth-subtext {
      font-size: 13px;
      color: #64748b;
      margin: 0;
      line-height: 1.45;
    }
  }

  .brand-icon {
    flex-shrink: 0;
    margin-left: 12px;
  }
}

.agreements-search-box {
  margin-bottom: 14px;
  :deep(.el-input__inner) {
    border-radius: 10px;
    height: 40px;
    border-color: #e2e8f0;
    font-size: 13px;
    &:focus {
      border-color: #3b82f6;
    }
  }
}

/* Completion Banner */
.completion-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 10px 14px;
  border-radius: 10px;
  margin-bottom: 14px;
  animation: fadeIn 0.3s ease;

  .banner-icon {
    color: #10b981;
    font-size: 20px;
    display: flex;
    align-items: center;
  }

  .banner-text {
    display: flex;
    flex-direction: column;
    strong {
      color: #065f46;
      font-size: 13px;
      font-weight: 600;
    }
    span {
      color: #047857;
      font-size: 12px;
    }
  }
}

/* Progress Bar Card */
.progress-bar-card {
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

  .progress-info-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;

    .progress-label {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      i {
        color: #2563eb;
      }
    }

    .progress-count {
      color: #2563eb;
    }
  }

  .progress-track {
    width: 100%;
    height: 5px;
    background: #e2e8f0;
    border-radius: 999px;
    overflow: hidden;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #3b82f6, #10b981);
      border-radius: 999px;
      transition: width 0.4s ease;
    }
  }
}

/* Skeletons */
.pending-docs-loading {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;

  .skeleton-group-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 14px;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .skeleton-group-header {
      display: flex;
      gap: 10px;
      align-items: center;
      padding-bottom: 10px;
      border-bottom: 1px solid #f1f5f9;

      .skeleton-icon {
        width: 34px;
        height: 34px;
        border-radius: 8px;
        background: #f1f5f9;
      }
      .skeleton-header-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 5px;
        .skeleton-title {
          width: 40%;
          height: 12px;
        }
        .skeleton-sub {
          width: 60%;
          height: 9px;
        }
      }
    }

    .skeleton-doc-row {
      display: flex;
      gap: 10px;
      align-items: center;
      .skeleton-doc-icon {
        width: 30px;
        height: 30px;
        border-radius: 6px;
        background: #f1f5f9;
      }
      .skeleton-doc-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        .skeleton-doc-name {
          width: 50%;
          height: 11px;
        }
        .skeleton-doc-meta {
          width: 30%;
          height: 9px;
        }
      }
    }

    .skeleton-line {
      background: #f1f5f9;
      border-radius: 4px;
    }
  }
}

/* Empty State */
.pending-docs-empty {
  background: #ffffff;
  border-radius: 14px;
  padding: 36px 20px;
  text-align: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  margin-bottom: 20px;

  .empty-icon-circle {
    width: 60px;
    height: 60px;
    background: #f0fdf4;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
  }

  .empty-title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 6px;
  }

  .empty-subtitle {
    font-size: 13px;
    color: #64748b;
    max-width: 320px;
    margin: 0 auto 18px;
    line-height: 1.45;
  }

  .empty-action {
    display: flex;
    justify-content: center;
  }
}

/* Groups Container */
.groups-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 18px;
}

.group-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;

  /* Group Header */
  .group-header {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 14px;

    .group-header-main {
      display: flex;
      gap: 10px;
      align-items: flex-start;
    }

    .group-avatar-icon {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: #eff6ff;
      color: #2563eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 17px;
      flex-shrink: 0;
    }

    .group-title-info {
      flex: 1;
      min-width: 0;

      .group-name-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        margin-bottom: 3px;

        .group-name {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          text-transform: capitalize;
        }

        .group-badge-count {
          font-size: 11px;
          font-weight: 600;
          color: #2563eb;
          background: #dbeafe;
          padding: 1px 7px;
          border-radius: 999px;
          white-space: nowrap;
        }
      }

      .group-meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        font-size: 11px;
        color: #64748b;

        .group-meta-item {
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
      }
    }
  }

  /* Group Documents List */
  .group-docs-list {
    display: flex;
    flex-direction: column;
  }

  /* Group Card Footer Actions */
  .group-card-footer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    flex-wrap: wrap;
    gap: 10px;

    .group-status-hint {
      font-size: 12px;
      font-weight: 500;

      .text-success-hint {
        color: #059669;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .text-pending-hint {
        color: #64748b;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
    }

    .group-buttons-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;

      .btn-group-action {
        font-size: 12.5px;
        font-weight: 600;
        padding: 5px 14px;
        border-radius: 6px;
        height: 32px;
        transition: all 0.15s ease;
      }

      .btn-group-reject {
        color: #dc2626;
        border-color: #fca5a5;
        background: #ffffff;

        &:hover {
          background: #fee2e2;
          border-color: #ef4444;
          color: #b91c1c;
        }
      }

      .btn-group-accept {
        background: #2563eb;
        border-color: #2563eb;
        color: #ffffff;

        &:hover:not(.is-disabled) {
          background: #1d4ed8;
          border-color: #1d4ed8;
        }

        &.is-disabled,
        &.is-disabled:hover,
        &.is-disabled:focus,
        &:disabled {
          background: #93c5fd;
          border-color: #93c5fd;
          color: #ffffff;
          cursor: not-allowed;
          opacity: 0.65;
        }
      }
    }
  }
}

/* Document Item Row */
.doc-item-row {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fdfdfd;
  }

  &.is-accepted {
    background-color: #fafdfb;
  }

  &.is-required:not(.is-accepted) {
    border-left: 3px solid #ef4444;
  }

  .doc-item-main {
    display: flex;
    gap: 10px;
    align-items: center;
    flex: 1;
    min-width: 0;
    cursor: pointer;
  }

  .doc-item-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #eff6ff;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;

    &.is-accepted-icon {
      background: #ecfdf5;
      color: #10b981;
    }

    &.is-pdf {
      background: #fee2e2;
      color: #ef4444;
    }

    &.is-img {
      background: #fdf4ff;
      color: #a855f7;
    }
  }

  .doc-item-info {
    flex: 1;
    min-width: 0;

    .doc-title-row {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 3px;
      flex-wrap: wrap;

      .doc-name {
        font-size: 13.5px;
        font-weight: 600;
        color: #1e293b;
        word-break: break-word;
      }
    }

    .doc-meta-row {
      display: flex;
      align-items: center;
      gap: 5px;
      flex-wrap: wrap;
    }
  }

  .badge-required {
    font-size: 10px;
    font-weight: 700;
    color: #dc2626;
    background: #fee2e2;
    padding: 1px 5px;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .badge-status-accepted {
    font-size: 11px;
    font-weight: 600;
    color: #059669;
    background: #d1fae5;
    padding: 1px 6px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .badge-status-pending {
    font-size: 11px;
    font-weight: 600;
    color: #d97706;
    background: #fef3c7;
    padding: 1px 6px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .meta-tag {
    font-size: 11px;
    color: #64748b;
    background: #f1f5f9;
    padding: 1px 5px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 3px;

    &.meta-ext {
      font-weight: 700;
      color: #ef4444;
      background: #fee2e2;
    }
  }

  /* Small & Professional Action Buttons */
  .doc-item-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;

    .btn-doc-small {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 12px;
      font-weight: 600;
      padding: 5px 10px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s ease;
      border: 1px solid transparent;
      outline: none;
      white-space: nowrap;

      &.btn-review {
        background: #2563eb;
        color: #ffffff;

        &:hover {
          background: #1d4ed8;
        }

        &.btn-accepted {
          background: #f1f5f9;
          color: #334155;
          border-color: #cbd5e1;

          &:hover {
            background: #e2e8f0;
          }
        }
      }

      &.btn-download {
        background: #ffffff;
        color: #475569;
        border-color: #cbd5e1;

        &:hover:not(:disabled) {
          background: #f8fafc;
          color: #1e293b;
          border-color: #94a3b8;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
}

/* Sticky Footer CTA */
.dashboard-proceed-card {
  margin-top: 16px;
  margin-bottom: 14px;

  .btn-proceed {
    font-size: 14px;
    font-weight: 600;
    height: 44px;
    border-radius: 10px;
  }

  .do-it-later-action {
    display: flex;
    justify-content: center;
    margin-top: 8px;

    .btn-do-it-later {
      background: transparent;
      border: none;
      color: #64748b;
      font-size: 12.5px;
      font-weight: 600;
      padding: 4px 8px;
      cursor: pointer;
      border-radius: 4px;
      transition: color 0.15s ease;

      &:hover {
        color: #1e293b;
        text-decoration: underline;
      }
    }
  }
}

.help-section {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 6px;
  margin-bottom: 20px;

  .support-link {
    color: #2563eb;
    text-decoration: none;
    font-weight: 600;
    &:hover {
      text-decoration: underline;
    }
  }
}

/* Modal Styling */
.modal_doc_review {
  :deep(.el-dialog__header) {
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
    .el-dialog__title {
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
    }
  }

  :deep(.el-dialog__body) {
    padding: 14px;
  }

  :deep(.el-dialog__footer) {
    padding: 10px 18px;
    border-top: 1px solid #e2e8f0;
  }
}

.doc-modal-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.doc-modal-meta-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  .doc-modal-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .doc-modal-header-actions {
    display: flex;
    align-items: center;
    gap: 6px;

    .btn-modal-action {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11.5px;
      font-weight: 600;
      padding: 5px 10px;
      border-radius: 6px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #334155;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #94a3b8;
      }
    }
  }
}

.doc-preview-viewport {
  width: 100%;
  height: 52vh;
  min-height: 300px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;

  .doc-iframe-container,
  .doc-image-preview-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .doc-preview-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  .doc-preview-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .doc-no-preview-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #94a3b8;
    i {
      font-size: 36px;
      margin-bottom: 6px;
    }
  }
}

.doc-ack-notice-box {
  display: flex;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;

  .ack-notice-icon {
    font-size: 17px;
    color: #3b82f6;
    margin-top: 1px;
  }

  .ack-notice-text {
    display: flex;
    flex-direction: column;
    font-size: 12.5px;
    color: #334155;
    line-height: 1.4;

    strong {
      color: #0f172a;
      margin-bottom: 2px;
    }
  }

  &.is-ack-done {
    background: #ecfdf5;
    border-color: #a7f3d0;
    .ack-notice-icon {
      color: #10b981;
    }
    .ack-notice-text {
      color: #065f46;
      strong {
        color: #065f46;
      }
    }
  }
}

.dialog-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
